<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmCompanyDeleteDialog from 'dashboard/components-next/Companies/CompanyDetail/ConfirmCompanyDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  selectedCompany: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const companiesStore = useCompaniesStore();

const [showDeleteSection, toggleDeleteSection] = useToggle();
const confirmDeleteDialogRef = ref(null);
const isDeleting = computed(() => companiesStore.uiFlags.deletingItem);

const openConfirmDeleteDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef.open();
};

const handleConfirmDelete = async () => {
  try {
    await companiesStore.delete(props.selectedCompany.id);
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.SUCCESS'));
    router.push({
      name: 'companies_dashboard_index',
      params: { accountId: route.params.accountId },
    });
  } catch {
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.ERROR'));
  }
};
</script>

<template>
  <Policy :permissions="['administrator']">
    <div class="flex flex-col items-start border-t border-n-strong px-6 py-5">
      <Button
        :label="t('COMPANIES.DETAIL.DELETE.BUTTON')"
        sm
        link
        slate
        class="hover:!no-underline text-n-slate-12"
        icon="i-lucide-chevron-down"
        trailing-icon
        @click="toggleDeleteSection()"
      />

      <div
        class="transition-all duration-300 ease-in-out grid w-full overflow-hidden"
        :class="
          showDeleteSection
            ? 'grid-rows-[1fr] opacity-100 mt-2'
            : 'grid-rows-[0fr] opacity-0 mt-0'
        "
      >
        <div class="overflow-hidden min-h-0">
          <span class="inline-flex text-n-slate-11 text-sm items-center gap-1">
            {{ t('COMPANIES.DETAIL.DELETE.SECTION_DESCRIPTION') }}
            <Button
              :label="t('COMPANIES.DETAIL.DELETE.CONFIRM')"
              sm
              ruby
              link
              @click="openConfirmDeleteDialog()"
            />
          </span>
        </div>
      </div>
    </div>
    <ConfirmCompanyDeleteDialog
      ref="confirmDeleteDialogRef"
      :company="selectedCompany"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
    />
  </Policy>
</template>
